import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { Box, Stack, TextField, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import CustomButton from '../../../components/button/ButtonComponent';
import { colors } from '../../../components/theme/theme';
import { useTranslation } from '../../../utils/translations';

import { LOGIN_LIMITS, sanitizeOtp, validateOtp } from './validators';

interface SecurityCodeStepProps {
    /** Last 4 digits when the code was sent by SMS; omitted for email. */
    smsLast4?: string;
    expiryMinutes: number;
    resendCooldownSeconds: number;
    /** Resolves after the verify attempt finishes (success or failure). */
    onVerify: (otp: string) => Promise<void> | void;
    onResend: () => Promise<void> | void;
    onCancel: () => void;
    disabled?: boolean;
    /** Overrides for reuse as the mobile-verification step. */
    title?: string;
    description?: string;
}

const formatMmSs = (seconds: number) =>
    `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;

export const SecurityCodeStep: React.FC<SecurityCodeStepProps> = ({
    smsLast4,
    expiryMinutes,
    resendCooldownSeconds,
    onVerify,
    onResend,
    onCancel,
    disabled,
    title,
    description,
}) => {
    const { t } = useTranslation();
    const [code, setCode] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    // Timestamps (not decrementing counters) keep the countdown/expiry accurate while backgrounded.
    const [resendAt, setResendAt] = useState(() => Date.now() + resendCooldownSeconds * 1000);
    const [expiresAt, setExpiresAt] = useState(() => Date.now() + expiryMinutes * 60 * 1000);
    const [now, setNow] = useState(() => Date.now());
    const inputRef = useRef<HTMLInputElement>(null);
    const resendRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 1000);
        const onVisible = () => setNow(Date.now());
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            clearInterval(id);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, []);

    const remaining = Math.max(0, Math.ceil((resendAt - now) / 1000));
    const isExpired = now >= expiresAt;

    useEffect(() => {
        if (isExpired) {
            setError(t('authFlow.securityCode.expired'));
            resendRef.current?.focus();
        }
    }, [isExpired, t]);

    const submit = useCallback(async () => {
        const validation = validateOtp(code);
        if (validation) {
            setError(validation);
            return;
        }
        if (isExpired) return;
        setBusy(true);
        setError(null);
        try {
            await onVerify(code);
        } finally {
            setBusy(false);
            setCode('');
            inputRef.current?.focus();
        }
    }, [code, isExpired, onVerify]);

    const resend = async () => {
        if (remaining > 0 || busy) return;
        setBusy(true);
        try {
            await onResend();
            setResendAt(Date.now() + resendCooldownSeconds * 1000);
            setExpiresAt(Date.now() + expiryMinutes * 60 * 1000);
            setNow(Date.now());
            setError(null);
            setCode('');
            inputRef.current?.focus();
        } finally {
            setBusy(false);
        }
    };

    const locked = busy || !!disabled;

    return (
        <Stack
            component="form"
            noValidate
            onSubmit={e => {
                e.preventDefault();
                submit();
            }}
            alignItems="center"
            gap={2}
            width="100%"
        >
            <Box
                sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    color: 'common.white',
                    display: 'grid',
                    placeItems: 'center',
                }}
            >
                <LockOutlinedIcon />
            </Box>
            <Typography variant="h5" component="h1">
                {title ?? t('authFlow.securityCode.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
                {description ??
                    (smsLast4
                        ? t('authFlow.securityCode.smsBody').replace('{{last4}}', smsLast4)
                        : t('authFlow.securityCode.emailBody'))}
            </Typography>

            <Box width="100%">
                <Typography variant="subtitle2" mb={0.5} component="label" htmlFor="login-otp">
                    {t('authFlow.securityCode.label')}
                </Typography>
                <TextField
                    id="login-otp"
                    fullWidth
                    autoFocus
                    inputRef={inputRef}
                    value={code}
                    disabled={locked}
                    error={!!error}
                    helperText={error ?? undefined}
                    placeholder="••••••"
                    onChange={e => {
                        setCode(sanitizeOtp(e.target.value));
                        if (error && !isExpired) setError(null);
                    }}
                    onPaste={e => {
                        e.preventDefault();
                        setCode(sanitizeOtp(e.clipboardData.getData('text')));
                    }}
                    autoComplete="one-time-code"
                    inputProps={{
                        'aria-label': 'Six-digit passcode',
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        maxLength: LOGIN_LIMITS.OTP_LENGTH,
                        style: { letterSpacing: '0.5em', textAlign: 'center', fontSize: 22 },
                    }}
                    FormHelperTextProps={{ role: 'alert' }}
                />
            </Box>

            <Stack direction="row" alignItems="center" gap={1}>
                <Typography variant="body2" color="text.secondary">
                    {t('authFlow.securityCode.didntReceive')}
                </Typography>
                {remaining > 0 ? (
                    <Stack direction="row" alignItems="center" gap={0.5} color="text.secondary">
                        <ScheduleIcon fontSize="inherit" />
                        <Typography variant="body2">
                            {t('authFlow.securityCode.resendIn').replace('{{time}}', formatMmSs(remaining))}
                        </Typography>
                    </Stack>
                ) : (
                    <CustomButton
                        ref={resendRef}
                        variant="text"
                        SMButton
                        fullWidth={false}
                        textColor={colors.errorPreset[10]}
                        onClick={resend}
                        disabled={locked}
                    >
                        {t('authFlow.securityCode.resend')}
                    </CustomButton>
                )}
            </Stack>
            <Typography variant="caption" color="text.secondary" textAlign="center">
                {t('authFlow.securityCode.helper').replace('{{minutes}}', String(expiryMinutes))}
            </Typography>

            <CustomButton
                type="submit"
                variant="contained"
                disabled={locked || isExpired || code.length !== LOGIN_LIMITS.OTP_LENGTH}
            >
                {t('authFlow.securityCode.submit')}
            </CustomButton>
            <CustomButton variant="text" disabled={busy} onClick={onCancel}>
                {t('authFlow.securityCode.cancel')}
            </CustomButton>
        </Stack>
    );
};
